import { Navigate } from 'react-router';
import { useActiveLocation } from '../../hooks/useActiveLocation';
import { useGrantedPermissions } from '../../hooks/useHasPermission';
import { useAppSelector } from '../../store/hooks';
import {
  selectAreAccountPermissionsLoaded,
  selectAreLocationPermissionsLoaded
} from '../../store/permissions/permissionsSlice';
import ROUTES, {
  GLOBAL_SETTINGS_PERMISSIONS,
  LOCATION_SETTINGS_PERMISSIONS,
  locationPath,
  pagePermissions,
  permittedPages
} from '../../routes/routeConstants';

const LOCATION_SETTINGS_PERMISSION_LIST = pagePermissions(LOCATION_SETTINGS_PERMISSIONS);
const GLOBAL_SETTINGS_PERMISSION_LIST = pagePermissions(GLOBAL_SETTINGS_PERMISSIONS);

// Resolves a bare settings entry point (/settings or /locations/:id/settings) to
// the first settings subpage the user may open: a location-scoped one at the
// active location when they hold its permission (for opening days the
// account-wide LOCATION_ADMIN, so a user holding only that lands there rather
// than on Settings → Centers), otherwise the first global one, otherwise home.
// Each subpage sits behind its own permission, so a fixed default would bounce
// every user who lacks that particular one.
export function SettingsRedirect() {
  const { activeLocationId, isLoading } = useActiveLocation();
  const areAccountPermissionsLoaded = useAppSelector(selectAreAccountPermissionsLoaded);
  const areLocationPermissionsLoaded = useAppSelector(selectAreLocationPermissionsLoaded);
  const grantedLocationPermissions = useGrantedPermissions(LOCATION_SETTINGS_PERMISSION_LIST);
  const grantedGlobalPermissions = useGrantedPermissions(GLOBAL_SETTINGS_PERMISSION_LIST);

  // Redirecting before the permissions have landed would pick a page on the
  // strength of "nothing granted yet" and send the user the wrong way.
  const isWaitingForLocation = activeLocationId !== null && !areLocationPermissionsLoaded;
  if (isLoading || !areAccountPermissionsLoaded || isWaitingForLocation) return null;

  const [locationPage] = permittedPages(LOCATION_SETTINGS_PERMISSIONS, grantedLocationPermissions);
  if (activeLocationId && locationPage) {
    return (
      <Navigate to={locationPath(activeLocationId, ROUTES.settings, locationPage.segment)} replace />
    );
  }

  const [globalPage] = permittedPages(GLOBAL_SETTINGS_PERMISSIONS, grantedGlobalPermissions);
  if (globalPage) {
    return <Navigate to={`/${ROUTES.settings}/${globalPage.segment}`} replace />;
  }

  return <Navigate to={`/${ROUTES.locations}`} replace />;
}
